// ============================================================
// ANSICHTSSACHE-AGENT – Login Server Action
// ============================================================
// Das Formular zeigt auf diese Funktion, nicht auf einen
// Klickhorcher im Browser. Der Unterschied ist keine
// Geschmacksfrage: ein `<form onSubmit={…}>` ohne `method`
// schickt der Browser, solange React noch nicht hydriert hat,
// als GET an dieselbe Adresse. E-Mail und Passwort stehen dann
// im Klartext in der Adresszeile, im Verlauf und im Server-Log —
//
//   GET /login?email=…&password=…
//
// — nachweisbar in `dev-server.log` vom 15.09.2026. Zeigt das
// Formular dagegen auf eine Server Action, rendert React
// `method="post"` und die Kennung der Aktion mit: derselbe
// Submit landet als POST hier und meldet an, statt die
// Zugangsdaten zu verteilen. Ohne JavaScript ebenso.
// ============================================================

"use server";

import { redirect } from "next/navigation";
import { zielNachAnmeldung } from "@/lib/auth/nach-anmeldung";
import { signIn } from "@/lib/auth";
import { CODE_FALSCH, CODE_FEHLT } from "@/lib/auth/zweiter-faktor";
import { CODE_GEDROSSELT } from "@/lib/auth/anmelde-gedrosselt";
import { FENSTER_MINUTEN } from "@/lib/auth/anmelde-drossel";
import { istWeiterleitung } from "@/lib/weiterleitung";

/**
 * `null`, solange nichts schiefgegangen ist. `zweiterFaktor` heisst:
 * Kennwort stimmt, jetzt fehlt der Code aus der App — das Formular
 * zeigt dann das Feld dafuer (`lib/auth/zweiter-faktor.ts`).
 */
export type LoginZustand = { error?: string; zweiterFaktor?: boolean } | null;

export async function loginAction(
  _zustand: LoginZustand,
  formData: FormData,
): Promise<LoginZustand> {
  try {
    await signIn("credentials", {
      email: formData.get("email") as string,
      password: formData.get("password") as string,
      totpCode: (formData.get("totpCode") as string | null) ?? "",
      redirect: false,
    });
  } catch (error) {
    // Eine Weiterleitung ist kein Fehler. Sie muss durch —
    // vorher hat der Zweig sie eingefangen und als „Erfolg“
    // zurueckgegeben, worauf der Browser auf der Anmeldeseite
    // stehen blieb.
    if (istWeiterleitung(error)) throw error;
    // Vor „falsche Zugangsdaten“: beide Fehler des zweiten Faktors
    // SIND Anmeldefehler derselben Art, nur mit eigenem Code.
    const code = (error as { code?: unknown })?.code;
    if (code === CODE_GEDROSSELT) {
      return {
        error: `Zu viele Fehlversuche. Bitte warte ${FENSTER_MINUTEN} Minuten und versuche es dann noch einmal.`,
      };
    }
    if (code === CODE_FEHLT) return { zweiterFaktor: true };
    if (code === CODE_FALSCH) {
      return {
        zweiterFaktor: true,
        error: "Der Code stimmt nicht. Nimm den, den deine App gerade zeigt.",
      };
    }
    if (istFalscheZugangsdaten(error)) return { error: "E-Mail oder Passwort ist falsch." };
    return { error: "Ein unerwarteter Fehler ist aufgetreten." };
  }

  // Ausserhalb des `try`: `redirect` arbeitet, indem es wirft.
  // Kam jemand vom Stempelchip an der Tuer, geht es dorthin zurueck.
  redirect(await zielNachAnmeldung("/dashboard"));
}

function istFalscheZugangsdaten(error: unknown): boolean {
  const { type, code } = (error ?? {}) as { type?: unknown; code?: unknown };
  return type === "CredentialsSignin" || code === "credentials";
}
